import { AuthenticationDetails, CognitoUser, CognitoUserSession } from "amazon-cognito-identity-js";
import { MobXProviderContext } from "mobx-react";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Password } from "primereact/password";
import { Toast } from "primereact/toast";
import { classNames } from "primereact/utils";
import React, { useContext, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { RouterAddress } from "../../../constants/RouterAddress";
import { redirectToHomeForAuthMode } from "../../../utils/stringUtils";
import AuthLayout from "../components/AuthLayout";
import { appFormSectionStyle } from "../../../components/general/uiSurfaceStyles";
import { PasswordPolicyFooter, passwordHeader } from "../constants/passwordPolicy";

const ChangePassword = () => {
  const toast = React.useRef<Toast>(null);
  const location = useLocation();
  const email = location?.state?.email || "";
  const { authStore } = useContext(MobXProviderContext);
  const isTemporaryPassword = location?.state?.isTemporaryPassword === true;
  const pendingNewPasswordUser = authStore.getPendingNewPasswordUser();
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();

  const defaultValues = {
    otp: '',
    password: '',
    confirmPassword: '',
  }
  const { control, formState: { errors }, handleSubmit, reset, watch } = useForm({ defaultValues });
  const passwordValue = watch("password", "");

  const getFormErrorMessage = (name) => {
    return errors[name] && <small className="p-error">{errors[name].message}</small>
  };

  const onSubmit = async (data: any) => {
    setLoading(true);
    setErrorMessage("");

    const { otp, password } = data;
    const cognitoUser = isTemporaryPassword && pendingNewPasswordUser
      ? pendingNewPasswordUser
      : new CognitoUser({
        Username: email,
        Pool: authStore.userPoolProps,
      });

    const finishLogin = async (session: CognitoUserSession) => {
      authStore.clearPendingNewPasswordUser();
      authStore.setUserProperties(session, cognitoUser.getUsername());
      await authStore.loadUserContext(true).catch(() => null);
      navigate(redirectToHomeForAuthMode(authStore.getAuthMode(), authStore.getUserRoleType()));
      window.location.reload();
    };

    if (isTemporaryPassword) {
      cognitoUser.completeNewPasswordChallenge(password, {}, {
        onSuccess: (session: CognitoUserSession) => {
          void finishLogin(session);
        },
        customChallenge: () => {
          cognitoUser.setAuthenticationFlowType("USER_PASSWORD_AUTH");
          cognitoUser.authenticateUser(
            new AuthenticationDetails({
              Username: cognitoUser.getUsername(),
              Password: password,
            }),
            {
              onSuccess: (session: CognitoUserSession) => {
                void finishLogin(session);
              },
              onFailure: (error) => {
                console.error(error);
                setErrorMessage(error.message);
                setLoading(false);
              },
            },
          );
        },
        onFailure(err) {
          console.error(err);
          setErrorMessage(err.message);
          setLoading(false);
        },
      });
      return;
    }

    cognitoUser.confirmPassword(otp, password, {
      onSuccess() {
        setLoading(false);
        reset();
        toast.current?.show({ severity: 'success', summary: 'Password changed successfully', detail: 'Redirecting to login page', life: 3000 });
        setTimeout(() => {
          navigate(RouterAddress.LOGIN_PAGE);
        }, 3000);
      },
      onFailure(err) {
        console.error(err);
        setErrorMessage(err.message);
        setLoading(false);
      },
    });
  };

  return (
    <AuthLayout title={isTemporaryPassword ? "Change Password" : "Reset Password"} subtitle={isTemporaryPassword ? "Choose a new password to finish signing in." : "Enter the emailed code and choose a new password for your account."}>
      <Toast ref={toast} />
      <form onSubmit={handleSubmit(onSubmit)} className="justify-content-center align-items-center flex-column flex m-4" style={appFormSectionStyle}>

        {!isTemporaryPassword ? <div className="field">
          <span className="p-float-label">
            <Controller name="otp" control={control} rules={{ required: 'OTP is required.' }} render={({ field, fieldState }) => (
              <InputText id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
            )} />
            <label htmlFor="otp" className={classNames({ 'p-error': errors.otp })}>OTP</label>
          </span>
          {getFormErrorMessage('otp')}
        </div> : null}

        <div className="field">
          <span className="p-float-label">
            <Controller name="password" control={control} rules={{ required: 'Password is required.' }} render={({ field, fieldState }) => (
              <Password id={field.name} {...field} feedback={true} toggleMask
                className={classNames({ 'p-invalid': fieldState.invalid })} header={passwordHeader} footer={<PasswordPolicyFooter password={passwordValue} />} />
            )} />
            <label htmlFor="password" className={classNames({ 'p-error': errors.password })}>Password</label>
          </span>
          {getFormErrorMessage('password')}
        </div>

        <div className="field">
          <span className="p-float-label">
            <Controller name="confirmPassword" control={control} rules={{
              required: 'Confirm Password is required.',
              validate: value => value === control._formValues.password || 'Passwords do not match.'
            }}
              render={({ field, fieldState }) => (
                <Password id={field.name} {...field} feedback={false} className={classNames({ 'p-invalid': fieldState.invalid })} />
              )} />
            <label htmlFor="confirmPassword" className={classNames({ 'p-error': errors.confirmPassword })}>Confirm Password</label>
          </span>
          {getFormErrorMessage('confirmPassword')}
        </div>

        <div className="p-error">{errorMessage}</div>

        <Button type="submit" label="Submit" severity="warning" rounded className="mt-2 p-button-outlined" loading={loading} />

        <div className="grid" style={{ minWidth: "70%", alignItems: "center", textAlign: "center", marginTop: "1em" }}>
          <p className="col">
            Back to - &nbsp;
            <a onClick={() => navigate(RouterAddress.LOGIN_PAGE)}>
              Login
            </a>
          </p>
        </div>
      </form>
    </AuthLayout>
  );
};

export default ChangePassword;
